// Plan Service - Manage saved retirement plans
// FIXED: Saves expense adjustments and full goal structure
import { supabase, isSupabaseConfigured } from './supabaseClient';

/**
 * Save comprehensive calculator data (early save - right after Freedom Number)
 * @param {object} comprehensiveData - Comprehensive calculator inputs
 * @param {number} freedomNumber - Calculated freedom number
 * @param {object} goals - Optional goals data
 * @param {array} expenseAdjustments - Optional expense adjustments
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const saveComprehensiveOnly = async (
  comprehensiveData,
  freedomNumber,
  goals = null,
  expenseAdjustments = null
) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' };
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if user already has a plan
    const { data: existingPlans } = await supabase
      .from('saved_plans')
      .select('id, comprehensive_data')
      .eq('user_id', user.id)
      .limit(1);

    const planToSave = {
      user_id: user.id,
      plan_name: 'My Retirement Plan',
      plan_type: 'complete',
      initial_corpus: comprehensiveData.currentCorpus || 0,
      yearly_expense: comprehensiveData.monthlyExpenses * 12 || 0,
      years: comprehensiveData.lifeExpectancy - comprehensiveData.retirementAge || 30,
      equity_allocation: 50,
      debt_allocation: 40,
      gold_allocation: 10,
      expense_reductions: [],
      comprehensive_data: {
        comprehensive: comprehensiveData,
        freedomNumber: freedomNumber,
        expenseAdjustments: expenseAdjustments || [],
        goals: {},
        stressTestCompleted: false,
        stressTestResults: null,
        totalFreedomNumber: null,
      },
      last_success_rate: null,
      last_simulation_date: new Date().toISOString(),
      notes: '',
      is_favorite: false,
    };

    if (existingPlans && existingPlans.length > 0) {
      // UPDATE existing plan - preserve goals, update comprehensive and adjustments
      const existingData = existingPlans[0].comprehensive_data || {};
      planToSave.comprehensive_data = {
        comprehensive: comprehensiveData,
        freedomNumber: freedomNumber,
        expenseAdjustments: expenseAdjustments || existingData.expenseAdjustments || [],
        // PRESERVE existing goals
        goals: existingData.goals || {},
        otherDescription: existingData.otherDescription || '',
        totalFreedomNumber: existingData.totalFreedomNumber || null,
        // Clear stress test since inputs changed
        stressTestCompleted: false,
        stressTestResults: null,
      };

      const { data, error } = await supabase
        .from('saved_plans')
        .update(planToSave)
        .eq('id', existingPlans[0].id)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data, isUpdate: true };
    } else {
      // INSERT new plan
      const { data, error } = await supabase
        .from('saved_plans')
        .insert([planToSave])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data, isUpdate: false };
    }
  } catch (error) {
    console.error('Error saving comprehensive data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if user has an existing plan
 */
export const checkExistingPlan = async () => {
  if (!isSupabaseConfigured()) {
    return { success: false, hasPlan: false, error: 'Database not configured' };
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, hasPlan: false, error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('saved_plans')
      .select('*')
      .eq('user_id', user.id)
      .limit(1);

    if (error) throw error;

    if (data && data.length > 0) {
      return { success: true, hasPlan: true, plan: data[0] };
    }
    return { success: true, hasPlan: false };
  } catch (error) {
    console.error('Error checking existing plan:', error);
    return { success: false, hasPlan: false, error: error.message };
  }
};

/**
 * Save or update a plan (overwrite mode)
 * FIXED: Saves full goal structure and expense adjustments
 */
export const savePlan = async (planData) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' };
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if user already has a plan
    const { data: existingPlans } = await supabase
      .from('saved_plans')
      .select('id')
      .eq('user_id', user.id)
      .limit(1);

    // Build goals with full structure
    const goalsToSave = {};
    if (planData.goals) {
      Object.entries(planData.goals).forEach(([key, value]) => {
        if (value && (value.modified === true || value.amount > 0)) {
          goalsToSave[key] = {
            amount: value.amount || 0,
            modified: true,
          };
          if (key === 'other' && value.description) {
            goalsToSave[key].description = value.description;
          }
        }
      });
    }

    const planToSave = {
      user_id: user.id,
      plan_name: planData.planName || planData.plan_name || 'My Retirement Plan',
      plan_type: 'complete',
      initial_corpus: planData.comprehensive?.currentCorpus || planData.initial_corpus || 0,
      yearly_expense: planData.comprehensive?.monthlyExpenses * 12 || planData.yearly_expense || 0,
      years:
        planData.comprehensive?.lifeExpectancy - planData.comprehensive?.retirementAge ||
        planData.years ||
        30,
      equity_allocation: planData.stressTest?.equityAllocation || planData.equity_allocation || 50,
      debt_allocation: planData.stressTest?.debtAllocation || planData.debt_allocation || 40,
      gold_allocation: planData.stressTest?.goldAllocation || planData.gold_allocation || 10,
      expense_reductions:
        planData.stressTest?.expenseReductions || planData.expense_reductions || [],
      comprehensive_data: {
        comprehensive: planData.comprehensive,
        freedomNumber: planData.calculatedFreedomNumber,
        expenseAdjustments: planData.expenseAdjustments || [],
        goals: goalsToSave,
        otherDescription: planData.goals?.other?.description || '',
        stressTestCompleted: planData.stressTestCompleted || false,
        stressTestResults: planData.stressTestResults || null,
        totalFreedomNumber: planData.totalFreedomNumber,
      },
      last_success_rate:
        planData.stressTestResults?.successRate || planData.last_success_rate || null,
      last_simulation_date: new Date().toISOString(),
      notes: planData.notes || '',
      is_favorite: planData.is_favorite || false,
    };

    if (existingPlans && existingPlans.length > 0) {
      const { data, error } = await supabase
        .from('saved_plans')
        .update(planToSave)
        .eq('id', existingPlans[0].id)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } else {
      const { data, error } = await supabase
        .from('saved_plans')
        .insert([planToSave])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    }
  } catch (error) {
    console.error('Error saving plan:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all plans for current user
 */
export const getMyPlans = async () => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' };
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('saved_plans')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching plans:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a specific plan by ID
 */
export const getPlan = async (planId) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('saved_plans')
      .select('*')
      .eq('id', planId)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching plan:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a plan
 */
export const updatePlan = async (planId, updates) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' };
  }

  try {
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('saved_plans')
      .update(updateData)
      .eq('id', planId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating plan:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a plan
 */
export const deletePlan = async (planId) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' };
  }

  try {
    const { error } = await supabase.from('saved_plans').delete().eq('id', planId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting plan:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Toggle favorite status
 */
export const toggleFavorite = async (planId, isFavorite) => {
  return updatePlan(planId, { is_favorite: isFavorite });
};
